import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { draftToPayload, useCreateImmunization } from 'hooks/useImmunizations';
import type { ImmunizationDraft } from 'types/immunization';
import { getApiErrorMessage } from 'utils/apiError';

/** Lives only in the frontend, like the notes, until the recording has produced a result. */
export function useImmunizationDraft() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ImmunizationDraft | null>(null);
  const { mutateAsync: createImmunization } = useCreateImmunization();

  const updateDraft = useCallback((changes: Partial<ImmunizationDraft>) => {
    setDraft((current) => (current ? { ...current, ...changes } : current));
  }, []);

  const clearDraft = useCallback(() => setDraft(null), []);

  const commit = useCallback(
    (interactionId: string, medatixxId: string) => {
      if (!draft) return Promise.resolve();
      // The block and the hidden alert stay until the store has answered.
      return createImmunization(draftToPayload(draft, interactionId, medatixxId)).then(
        () => setDraft(null),
        (error: Error) => {
          toast.error(getApiErrorMessage(error, t));
          setDraft(null);
        }
      );
    },
    [draft, createImmunization, t]
  );

  return { draft, setDraft, updateDraft, clearDraft, commit };
}
